import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import camelcaseKeys from 'camelcase-keys';
import { DateTime, Duration } from 'luxon';
import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import Prismic from 'prismic-javascript';
import React from 'react';
import { Badge, Card, Col, Nav, Row } from 'react-bootstrap';
import SVG from 'react-inlinesvg';
import Head from '../../../../components/head';
import Layout from '../../../../components/Layout';
import OpenGraphDescription from '../../../../components/OpenGraphDescription';
import OpenGraphTitle from '../../../../components/OpenGraphTitle';
import PageTitle from '../../../../components/PageTitle';
import Paginator from '../../../../components/Paginator';
import Section from '../../../../components/Section';
import { FranchiseeProvider } from '../../../../context/Franchisee';
import { FranchiseeState } from '../../../../context/Franchisee/franchisee.types';
import Gataware from '../../../../SDKs/Gataware';
import { PrismicClient } from '../../../../SDKs/Prismic/prismic.config';
import { BlogItemDataCamelCase } from '../../../../SDKs/Prismic/prismic.types';
const iconPageNotFound = '/images/icon-404.svg';

export const getStaticProps: GetStaticProps = async (context) => {
  // const { req } = context;
  // console.log(`The params are: ${JSON.stringify(context.params)}`);
  const { pageNumber, franchiseeSlug } = context.params;

  const response = await Gataware.getLocations({
    slugs: [franchiseeSlug.toLocaleString()],
  }).then(({ data }) => JSON.parse(data));

  const franchisees = await response.data
    .filter(({ slug }) => franchiseeSlug === slug)
    .map((franchisee) => camelcaseKeys(franchisee));

  if (!franchisees) {
    return {
      notFound: true,
      revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
    };
  }

  // if the user tries to pass in non-numbers for a page number;
  if (/\D+/g.test(pageNumber.toString())) {
    return { notFound: true };
  }

  const [franchisee] = franchisees;

  // convert pageNumber to int.
  const numberOfPages = parseInt(pageNumber.toString(), 10);

  // const queryResult = await PrismicClient().query(
  //   Prismic.Predicates.at('document.type', 'blog-post'),
  //   {
  //     page: numberOfPages,
  //     orderings: '[my.blog-post.dateposted desc]',
  //     pageSize: 6
  //   }
  // );

  const queryResult = PrismicClient().query(
    Prismic.Predicates.any('my.blog-post.franchiseeslug', [
      franchisee.slug.toLowerCase(),
      'general',
    ]),
    {
      pageSize: 6,
      page: numberOfPages,
      orderings: '[my.blog-post.dateposted desc]',
    }
  );

  const {
    page,
    prev_page: prevPage,
    next_page: nextPage,
    results_per_page: resultsPerPage,
    results_size: resultsSize,
    total_pages: totalPages,
    results,
  } = await queryResult;

  const blogItems = await results
    .filter(
      ({ uid, data }) =>
        (uid !== null &&
          data.franchiseeslug === franchisee.slug.toLowerCase()) ||
        data.franchiseeslug === 'general'
    )
    .map((item) => camelcaseKeys({ ...item, data: camelcaseKeys(item.data) }));

  const meta = {
    page,
    prevPage,
    nextPage,
    resultsPerPage,
    resultsSize,
    totalPages,
  };

  return {
    props: {
      meta,
      blogItems,
      franchisee,
    },
    // revalidate: Duration.fromObject({ minutes: 15 }).as('seconds'),
    revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
  };
};

export const getStaticPaths = async () => {
  const paths = [{ params: { pageNumber: '1', franchiseeSlug: 'birmingham' } }];

  return {
    paths,
    fallback: 'blocking',
  };
};

interface BlogPageProps {
  /* add blog page props here */
  blogItems: Array<{
    id: string;
    firstPublicationDate: string;
    lastPublicationDate: string;
    type: string;
    slugs: Array<string>;
    tags: Array<string>;
    data: BlogItemDataCamelCase;
    uid: string;
    franchiseeslug: string;
  }>;
  meta: {
    page: number;
    prevPage?: number;
    nextPage?: number;
    resultsPerPage?: number;
    resultsSize: number;
    totalPages: number;
  };
  franchisee: FranchiseeState;
}

const Blog: React.FC<BlogPageProps> = ({
  blogItems,
  meta,
  franchisee,
}: BlogPageProps) => (
  <FranchiseeProvider franchiseeValue={franchisee}>
    <Layout>
      <Head
        slug='blog'
        title={`Blog | Two Maids | ${franchisee.name}`}
        description={`Get the Latest Posts for Two Maids ${franchisee.name}`}
      />
      <OpenGraphTitle title={`Blog | Two Maids | ${franchisee.name}`} />

      <OpenGraphDescription>
        {/* Add Blog description here */}
        {`Get the Latest Posts for Two Maids ${franchisee.name}`}
      </OpenGraphDescription>
      <div className='top-background-dots'></div>

      <Section lg={12} md={12}>
        {meta.resultsSize > 0 && (
          <PageTitle className=''>Two Maids Cleaning Blog</PageTitle>
        )}
        <Row style={{ justifyContent: 'space-between', rowGap: '4rem' }}>
          {meta.resultsSize ? (
            blogItems.map(({ data, id, firstPublicationDate, uid, tags }) => {
              const publishedDate = DateTime.fromISO(data.dateposted);
              const fallbackDate = DateTime.fromISO(firstPublicationDate);
              return (
                <Link key={uid} href={`/${franchisee.slug}/blog/${uid}/`}>
                  <Col
                    className='mb-4 text-center'
                    xs={12}
                    md={4}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card
                      key={id}
                      className='blog-card-new'
                      style={{ width: '100%', height: '100%' }}
                    >
                      <Row>
                        {data.banner.url && (
                          <Col xs={12} md={12}>
                            <Image
                              className='blog-card-image'
                              src={data.banner.url}
                              alt={data.banner.alt}
                              width='300px'
                              height='300px'
                              placeholder='blur'
                              blurDataURL={data.banner.url}
                            />
                          </Col>
                        )}
                        <Col xs={12} md={12}>
                          <Card.Title
                            className='px-4 pt-4 ellipsis blog-card-title'
                            style={{
                              fontSize: '18px',
                              lineHeight: '26px',
                              overflow: 'hidden',
                              fontWeight: 'bold',
                              color: 'var(--dark-pink)',
                              fontFamily: 'Gotham Medium',
                            }}
                          >
                            <span>{data.blogTitle[0]?.text}</span>
                          </Card.Title>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={12} className='px-4'>
                          <Card.Text style={{fontSize: 14, color: 'var(--dark-pink)'}}>
                            {
                              publishedDate ?
                                publishedDate.toLocaleString({ locale: "en-US" })
                                : fallbackDate.toLocaleString({ locale: "en-US" })
                            }
                          </Card.Text>
                        </Col>
                      </Row>
                      <Row>
                          {data.authorName !== null && (
                        <Col xs={12} md={12} className='pt-4'>
                            <>
                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: 'var(--dark-pink)',
                                }}
                              >
                                By: {data.authorName}
                              </span>
                  
                              <br />
                            </>
                        </Col>
                          )}
                      </Row>
                      <Row className='px-2 pb-4 badge-list'>
                          {tags.length > 0 && (
                            <>
                              <br />
                              {tags.slice(0, 6).map(
                                (tag) =>
                                  tag.length < 14 && (
                                    <Badge
                                      key={tag}
                                      className='px-2'
                                      style={{
                                        fontSize: '14px',
                                        color: 'var(--dark-pink)',
                                        fontFamily: 'Gotham Medium',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      {tag}
                                    </Badge>
                                  )
                              )}
                            </>
                          )}
                      </Row>
                    </Card>
                  </Col>
                </Link>
              );
            })
          ) : (
            <Col xs={10} lg={12} xl={10}>
              <SVG
                src={iconPageNotFound}
                title='A piece of paper'
                className='w-100 mb-4'
              />

              <h1 className='h2 text-center mb-5'>
                We&apos;re Sorry. Two Maids {franchisee.name} hasn&apos;t posted
                any content yet.
              </h1>

              <p className='text-center'>
                Maybe one of these links will help you find what you&apos;re
                looking for.
              </p>
              <Nav className='d-block d-lg-flex justify-content-center my-5'>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/locations/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Find a Store <FontAwesomeIcon icon={faSearch} />
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/services/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Cleaning Packages
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link>
                    <Link href='/book/'>
                      <a className='btn btn-outline-primary btn-block'>
                        Get a Quote
                      </a>
                    </Link>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          )}
        </Row>
        {meta.totalPages > 1 && (
          <Row className='justify-content-center'>
            <Col xs={12} md={8} className='d-flex justify-content-end'>
              <Paginator
                meta={meta}
                hrefBase={`/${franchisee.slug}/blog/page/`}
              />
            </Col>
          </Row>
        )}
      </Section>
    </Layout>
  </FranchiseeProvider>
);

// Blog.defaultProps = {
//   blogItems: [],
//   meta: ({
//     page: 1,
//     prevPage: null,
//     nextPage: null,
//     resultsPerPage: 6,
//     resultsSize: 0,
//     totalPages: 1,
//   }),
// };

export default Blog;
